import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import Layout from "../components/Layout";
import { OnboardingChecklist } from "../components/OnboardingChecklist";
import { LogoUploader, CompanyBadge } from "../components/LogoUploader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  DollarSign, Users, TrendingUp, AlertCircle, ArrowRight, Plus, Building2, Clock, Calendar,
  ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, Minus, LogIn,
  Calculator
} from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle,
} from "../components/ui/sheet";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import QuickPayForm from "../components/QuickPayForm";
import BalanceAuditSheet from "../components/BalanceAuditSheet";
import { AnnouncementsBanner } from "../components/AnnouncementsBanner";
import { DowngradeBanner } from "../components/DowngradeBanner";

// ---- Employee Dashboard ----
const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const { user, tenant } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payHistory, setPayHistory] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [dashRes, payRes] = await Promise.all([
          apiClient.get('/me/dashboard'),
          apiClient.get('/me/pay-rate-history').catch(() => ({ data: { history: [], hire_pay: null } })),
        ]);
        setData(dashRes.data);
        setPayHistory(payRes.data);
      } catch (err) {
        console.error("Failed to load employee dashboard:", err);
      } finally {
        setLoading(false);
      }
    };
    if (tenant) fetch();
    else setLoading(false);
  }, [tenant]);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const dateOnly = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    return new Date(dateOnly + 'T12:00:00').toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Layout>
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-28 bg-slate-200 rounded" />)}
          </div>
        </div>
      </Layout>
    );
  }

  if (!tenant) {
    return (
      <Layout>
        <div data-testid="employee-no-tenant" className="text-center py-16">
          <Building2 className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-[#0A192F] mb-2">No Company Yet</h2>
          <p className="text-slate-500">Please wait for your team admin to set up the company, or accept a pending invite.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="employee-dashboard" className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">
            Hi, {data?.employee_name || user?.name}
          </h1>
          <p className="text-slate-600 mt-1">
            Welcome to {tenant.company_name}. Here&apos;s your summary.
          </p>
        </div>

        <AnnouncementsBanner />
        <DowngradeBanner />

        {!data?.employee_id ? (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0A192F] mb-2">Profile Not Linked</h3>
              <p className="text-slate-500">Your account ({user?.email || user?.phone || user?.name}) is not yet linked to an employee record. Please ask your manager to add you with this contact.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <Card className="border border-slate-200">
                <CardContent className="p-5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Pay Rate</p>
                  <p className="text-2xl font-black tracking-tighter text-[#0A192F]" data-testid="emp-pay-rate">
                    {formatCurrency(data?.pay_value)}
                    <span className="text-xs font-normal text-slate-500 ml-1">
                      {data?.pay_type === 'hourly' ? '/hr' : data?.pay_type === 'daily' ? '/day' : '/mo'}
                    </span>
                  </p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Hours This Month</p>
                  <p className="text-2xl font-black tracking-tighter text-[#0D9488]" data-testid="emp-hours-month">
                    {data?.total_hours_this_month || 0}<span className="text-xs font-normal text-slate-500 ml-1">hrs</span>
                  </p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Total Earned</p>
                  <p className="text-2xl font-black tracking-tighter text-[#0A192F]" data-testid="emp-total-earned">
                    {formatCurrency(data?.total_earned)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Total Paid</p>
                  <p className="text-2xl font-black tracking-tighter text-[#10B981]" data-testid="emp-total-paid">
                    {formatCurrency(data?.total_paid)}
                  </p>
                </CardContent>
              </Card>
              <Card className="border border-slate-200">
                <CardContent className="p-5">
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Balance Due</p>
                  <p className={`text-2xl font-black tracking-tighter ${data?.balance_due > 0 ? 'text-[#E11D48]' : 'text-[#10B981]'}`} data-testid="emp-balance-due">
                    {formatCurrency(data?.balance_due)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Recent Time Entries */}
            <Card className="border border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="w-5 h-5 text-[#0D9488]" /> Recent Time Entries
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => navigate('/time-entries')} data-testid="view-all-time-btn">
                  View All <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </CardHeader>
              <CardContent>
                {data?.recent_time_entries?.length === 0 ? (
                  <p className="text-slate-500 text-center py-4">No time entries yet this month.</p>
                ) : (
                  <div className="space-y-2">
                    {data?.recent_time_entries?.map((entry) => (
                      <div key={entry.entry_id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-3">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-[#0A192F]">{formatDate(entry.worked_date)}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-[#0D9488] font-semibold">{entry.net_hours} hrs</span>
                          <Badge variant={entry.status === 'approved' ? 'default' : 'secondary'}>{entry.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pay Rate History */}
            {(() => {
              const rows = [];
              if (payHistory?.history?.length) rows.push(...payHistory.history);
              if (payHistory?.hire_pay) {
                // Only include the hire baseline when no history row already covers that date.
                const covered = rows.some(r => r.effective_date === payHistory.hire_pay.effective_date);
                if (!covered) rows.push(payHistory.hire_pay);
              }
              if (rows.length === 0) return null;

              // Sort: future first (top), then by effective_date desc.
              const sorted = [...rows].sort((a, b) => (b.effective_date || "").localeCompare(a.effective_date || ""));
              // Pairwise delta (for chevron up/down next to each row vs. the one *before* chronologically)
              const chronological = [...sorted].reverse();
              const deltaByDate = {};
              for (let i = 1; i < chronological.length; i++) {
                const prev = chronological[i - 1].pay_value ?? 0;
                const cur = chronological[i].pay_value ?? 0;
                deltaByDate[chronological[i].effective_date] = cur - prev;
              }

              return (
                <Card className="border border-slate-200" data-testid="emp-pay-history-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-[#0D9488]" /> Pay Rate History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {sorted.map((row, idx) => {
                        const delta = deltaByDate[row.effective_date] || 0;
                        const unit = row.pay_type === 'hourly' ? '/hr' : row.pay_type === 'daily' ? '/day' : '/mo';
                        return (
                          <div
                            key={`${row.effective_date}-${idx}`}
                            className={`flex items-center justify-between py-2.5 px-3 rounded-lg border ${row.is_future ? 'border-amber-200 bg-amber-50/40' : 'border-slate-100'}`}
                            data-testid={`emp-pay-history-row-${idx}`}
                          >
                            <div className="flex items-center gap-3 min-w-0">
                              <Calendar className="w-4 h-4 text-slate-400 shrink-0" />
                              <div className="min-w-0">
                                <p className="font-medium text-sm text-[#0A192F]">
                                  {formatDate(row.effective_date)}
                                  {row.is_future && <Badge className="ml-2 bg-amber-100 text-amber-700 text-[10px]">Upcoming</Badge>}
                                  {row.is_baseline && <Badge variant="outline" className="ml-2 text-[10px] text-slate-500">Hire</Badge>}
                                </p>
                                {row.reason && <p className="text-xs text-slate-500 truncate">{row.reason}</p>}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {delta > 0 && (
                                <span className="flex items-center text-xs text-emerald-600 font-semibold" data-testid="pay-delta-up">
                                  <ArrowUpRight className="w-3.5 h-3.5" />+{formatCurrency(Math.abs(delta))}
                                </span>
                              )}
                              {delta < 0 && (
                                <span className="flex items-center text-xs text-red-600 font-semibold" data-testid="pay-delta-down">
                                  <ArrowDownRight className="w-3.5 h-3.5" />-{formatCurrency(Math.abs(delta))}
                                </span>
                              )}
                              <span className="font-bold text-[#0A192F]">
                                {formatCurrency(row.pay_value)}
                                <span className="text-xs font-normal text-slate-500 ml-1">{unit}</span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Quick Actions for Employee */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="cursor-pointer border border-slate-200 hover:shadow-md hover:border-[#0D9488] transition-all duration-200 group"
                onClick={() => navigate('/time-entries')} data-testid="emp-log-time-action">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-[#CCFBF1] text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-colors">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A192F]">Log Time</h3>
                      <p className="text-sm text-slate-500">Submit your hours for today</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-[#0D9488] transition-colors" />
                  </div>
                </CardContent>
              </Card>
              <Card className="cursor-pointer border border-slate-200 hover:shadow-md hover:border-[#0D9488] transition-all duration-200 group"
                onClick={() => navigate('/schedule')} data-testid="emp-view-schedule-action">
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-[#CCFBF1] text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-colors">
                      <Calendar className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0A192F]">My Schedule</h3>
                      <p className="text-sm text-slate-500">View your upcoming shifts</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-[#0D9488] transition-colors" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

// ---- Owner/Manager Dashboard ----
const ManagerDashboard = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenant, refreshTenant } = useAuth();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetupDialog, setShowSetupDialog] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [timezone, setTimezone] = useState("America/New_York");
  const [companyLogo, setCompanyLogo] = useState("");
  const [creating, setCreating] = useState(false);

  // Payment-success handler — mirrors the Settings page. When Stripe
  // redirects the fresh-signup flow to /dashboard?session_id=... we poll
  // the checkout status until it flips to `paid`, then refresh the
  // tenant (so PlanBadge / limits update immediately) and show the toast.
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await apiClient.get(`/subscription/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          toast.success(`Payment successful! Your plan is now active.`);
          await refreshTenant();
          navigate("/dashboard", { replace: true });
          return;
        }
        if (attempts++ < 5) setTimeout(poll, 2000);
      } catch {
        /* silent — user can refresh Settings to see status */
      }
    };
    poll();
  }, [searchParams]);
  // Hours comparison state
  const toLocalDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  const [hoursWeekStart, setHoursWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    return toLocalDate(d);
  });
  const [hoursData, setHoursData] = useState(null);
  const [hoursLoading, setHoursLoading] = useState(false);
  // Quick action state
  const [employees, setEmployees] = useState([]);
  const [activeClocks, setActiveClocks] = useState([]);
  const [showPaySheet, setShowPaySheet] = useState(false);
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [showAuditSheet, setShowAuditSheet] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditInactive, setAuditInactive] = useState(true);
  const [savingAction, setSavingAction] = useState(false);
  const [timeForm, setTimeForm] = useState({
    employee_id: "", worked_date: toLocalDate(new Date()), worked_hours: "", break_hours: "0"
  });

  useEffect(() => {
    if (!tenant) {
      // Before offering company setup, check if this user was invited to
      // join an existing team. If so, route them to accept the invite so
      // they don't accidentally create a ghost tenant.
      (async () => {
        try {
          const res = await apiClient.get('/team/my-pending-invites');
          const inv = (res.data?.invites || [])[0];
          if (inv?.invite_token) {
            navigate(`/invite/${inv.invite_token}`, { replace: true });
            return;
          }
        } catch {
          // If the endpoint fails for any reason, fall through to setup.
        }
        setShowSetupDialog(true);
        setLoading(false);
      })();
    } else {
      fetchKPIs();
    }
  }, [tenant, navigate]);

  useEffect(() => {
    if (tenant) fetchHoursComparison();
  }, [tenant, hoursWeekStart]);

  const fetchHoursComparison = async () => {
    setHoursLoading(true);
    try {
      const res = await apiClient.get(`/dashboard/hours-comparison?week_start=${hoursWeekStart}`);
      setHoursData(res.data);
    } catch (err) {
      console.error("Failed to fetch hours comparison:", err);
    } finally {
      setHoursLoading(false);
    }
  };

  const navigateHoursWeek = (dir) => {
    const d = new Date(hoursWeekStart + 'T12:00:00');
    d.setDate(d.getDate() + dir * 7);
    setHoursWeekStart(toLocalDate(d));
  };

  const formatWeekRange = (ws) => {
    const start = new Date(ws + 'T12:00:00');
    const end = new Date(ws + 'T12:00:00');
    end.setDate(end.getDate() + 6);
    return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;
  };

  const fetchKPIs = async () => {
    try {
      const [kpiRes, empRes, clockRes] = await Promise.all([
        apiClient.get('/dashboard/kpis'),
        apiClient.get('/employees?status=active'),
        apiClient.get('/clock/active').catch(() => ({ data: { active_clocks: [] } }))
      ]);
      setKpis(kpiRes.data);
      setEmployees(empRes.data.employees);
      setActiveClocks(clockRes.data.active_clocks);
    } catch (error) {
      console.error("Failed to fetch KPIs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTenant = async () => {
    if (!companyName.trim()) return;
    setCreating(true);
    try {
      await apiClient.post('/tenants', { company_name: companyName, timezone });
      // Logo is optional and PUT is a separate step because it's a much
      // larger payload than the small tenant-create body.
      if (companyLogo) {
        try {
          await apiClient.put('/tenants/current', { logo: companyLogo });
        } catch (e) {
          console.warn("Logo upload skipped:", e);
        }
      }
      await refreshTenant();
      setShowSetupDialog(false);
      fetchKPIs();
    } catch (error) {
      console.error("Failed to create tenant:", error);
    } finally {
      setCreating(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const handleTimeSubmit = async (e) => {
    e.preventDefault();
    setSavingAction(true);
    try {
      await apiClient.post('/time-entries', {
        employee_id: timeForm.employee_id, worked_date: timeForm.worked_date,
        worked_hours: parseFloat(timeForm.worked_hours), break_hours: parseFloat(timeForm.break_hours || 0)
      });
      toast.success("Time entry added");
      setShowTimeDialog(false);
      setTimeForm({ employee_id: "", worked_date: toLocalDate(new Date()), worked_hours: "", break_hours: "0" });
      fetchKPIs();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add time entry");
    } finally { setSavingAction(false); }
  };

  const formatClockTime = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
  };

  const clockDuration = (checkIn) => {
    if (!checkIn) return "";
    const diff = (Date.now() - new Date(checkIn).getTime()) / 3600000;
    return `${diff.toFixed(1)}h`;
  };

  const kpiCards = [
    { title: "Total Outstanding", value: formatCurrency(kpis?.total_outstanding), icon: <AlertCircle className="w-6 h-6" />, color: "text-[#E11D48]", bgColor: "bg-red-50", testId: "total-outstanding-metric" },
    { title: "Paid This Month", value: formatCurrency(kpis?.total_paid_this_month), icon: <DollarSign className="w-6 h-6" />, color: "text-[#10B981]", bgColor: "bg-emerald-50", testId: "paid-this-month-metric" },
    { title: "Est. Monthly Payroll", value: formatCurrency(kpis?.estimated_payroll), icon: <TrendingUp className="w-6 h-6" />, color: "text-[#0D9488]", bgColor: "bg-teal-50", testId: "estimated-payroll-metric" },
    { title: "Active Employees", value: kpis?.total_employees || 0, icon: <Users className="w-6 h-6" />, color: "text-[#0A192F]", bgColor: "bg-slate-100", testId: "total-employees-metric" }
  ];

  const quickActions = [
    { title: "Record Payment", description: "Quick pay an employee", icon: <DollarSign className="w-5 h-5" />, onClick: () => setShowPaySheet(true), testId: "quick-pay-action" },
    { title: "Add Time Entry", description: "Log employee hours", icon: <TrendingUp className="w-5 h-5" />, onClick: () => setShowTimeDialog(true), testId: "add-time-action" },
    { title: "Balance Audit", description: "Verify every employee's math", icon: <Calculator className="w-5 h-5" />, onClick: () => setShowAuditSheet(true), testId: "balance-audit-action" },
    { title: "View Schedule", description: "Manage weekly schedule", icon: <Users className="w-5 h-5" />, onClick: () => navigate('/schedule'), testId: "view-schedule-action" }
  ];

  return (
    <Layout>
      <div data-testid="dashboard-page" className="space-y-8">
        <div className="flex items-center gap-3">
          {tenant && (
            <CompanyBadge logo={tenant.logo} name={null} logoSize={44} />
          )}
          <div>
            <h1 className="text-3xl font-bold text-[#0A192F] font-['Manrope'] tracking-tight">Dashboard</h1>
            <p className="text-slate-600 mt-1">
              {tenant ? `Welcome back! Here's your ${tenant.company_name} overview.` : 'Set up your company to get started.'}
            </p>
          </div>
        </div>

        <AnnouncementsBanner />
        <DowngradeBanner />

        <OnboardingChecklist />

        <Dialog open={showSetupDialog} onOpenChange={setShowSetupDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-[#0D9488]" /> Set Up Your Company
              </DialogTitle>
              <DialogDescription>Create your company profile to start using Nirvanta.</DialogDescription>
            </DialogHeader>
            {/* Post-signup checkout banner — when the visitor signed up via
                "Upgrade to X" on the landing page we tell them explicitly
                that they'll continue to Stripe as soon as the company is
                created, so the free-tier setup dialog doesn't feel like a
                bait-and-switch. */}
            {(() => {
              try {
                const raw = localStorage.getItem("pending_checkout");
                if (!raw) return null;
                const p = JSON.parse(raw);
                if (!p?.plan) return null;
                const label = p.plan === "pro" ? "Pro" : "Starter";
                return (
                  <div className="rounded-md bg-[#0D9488]/5 border border-[#0D9488]/30 px-3 py-2 text-xs text-[#0A192F]" data-testid="pending-checkout-banner">
                    Continuing to <span className="font-semibold">{label}</span> checkout after your company is created.
                  </div>
                );
              } catch { return null; }
            })()}
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="company-name">Company Name</Label>
                <Input id="company-name" placeholder="e.g., Joe's Restaurant" value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)} data-testid="company-name-input" />
              </div>
              <LogoUploader value={companyLogo} onChange={setCompanyLogo} size={72} label="Logo (optional)" />
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone</Label>
                <Select value={timezone} onValueChange={setTimezone}>
                  <SelectTrigger data-testid="timezone-select"><SelectValue placeholder="Select timezone" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="America/New_York">Eastern Time</SelectItem>
                    <SelectItem value="America/Chicago">Central Time</SelectItem>
                    <SelectItem value="America/Denver">Mountain Time</SelectItem>
                    <SelectItem value="America/Los_Angeles">Pacific Time</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-2 pt-4">
                <Button onClick={handleCreateTenant} disabled={!companyName.trim() || creating}
                  data-testid="create-company-btn" className="bg-[#0D9488] hover:bg-[#14B8A6]">
                  {creating ? "Creating..." : "Create Company"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} className="animate-pulse"><CardContent className="p-6"><div className="h-20 bg-slate-200 rounded"></div></CardContent></Card>
            ))}
          </div>
        ) : kpis && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {kpiCards.map((kpi, index) => (
              <Card key={index} className="kpi-card border border-slate-200 hover:shadow-md hover:border-slate-300 transition-all duration-200">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">{kpi.title}</p>
                      <p className={`text-3xl font-black tracking-tighter ${kpi.color}`} data-testid={kpi.testId}>{kpi.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg ${kpi.bgColor} ${kpi.color}`}>{kpi.icon}</div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {kpis && kpis.overdue_employees > 0 && (
          <Card className="border-[#E11D48] bg-red-50">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <AlertCircle className="w-5 h-5 text-[#E11D48]" />
                <div>
                  <p className="font-semibold text-[#E11D48]">
                    {kpis.overdue_employees} employee{kpis.overdue_employees > 1 ? 's have' : ' has'} outstanding balance
                  </p>
                  <p className="text-sm text-slate-600">Total: {formatCurrency(kpis.total_outstanding)}</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => navigate('/payments')}
                className="border-[#E11D48] text-[#E11D48] hover:bg-[#E11D48] hover:text-white" data-testid="view-overdue-btn">
                View & Pay <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        )}

        <div>
          <h2 className="text-xl font-semibold text-[#0A192F] mb-4 font-['Manrope']">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {quickActions.map((action, index) => (
              <Card key={index} className="cursor-pointer border border-slate-200 hover:shadow-md hover:border-[#0D9488] transition-all duration-200 group"
                onClick={action.onClick} data-testid={action.testId}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="p-3 rounded-lg bg-[#CCFBF1] text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-colors">{action.icon}</div>
                    <div>
                      <h3 className="font-semibold text-[#0A192F]">{action.title}</h3>
                      <p className="text-sm text-slate-500">{action.description}</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-slate-400 ml-auto group-hover:text-[#0D9488] transition-colors" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {kpis && kpis.total_employees === 0 && (
          <Card className="border-dashed border-2 border-slate-300">
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#0A192F] mb-2">No employees yet</h3>
              <p className="text-slate-500 mb-4">Add your first employee to start tracking payroll.</p>
              <Button onClick={() => navigate('/employees')} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="add-first-employee-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Employee
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Currently Working Section */}
        {activeClocks.length > 0 && (
          <Card className="border border-slate-200" data-testid="currently-working-section">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <LogIn className="w-5 h-5 text-[#10B981]" /> Currently Working
                <Badge className="bg-emerald-100 text-emerald-700 ml-2">{activeClocks.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {activeClocks.map(c => (
                  <div key={c.clock_id} className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                    <span className="font-medium text-sm text-[#0A192F]">{c.employee_name}</span>
                    <span className="text-xs text-slate-500">since {formatClockTime(c.check_in)}</span>
                    <Badge variant="outline" className="text-xs text-slate-500">{clockDuration(c.check_in)}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Expected vs Actual Hours Comparison */}
        {tenant && (
          <Card className="border border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#0D9488]" /> Expected vs Actual Hours
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigateHoursWeek(-1)} data-testid="hours-prev-week">
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-lg">
                  <Calendar className="w-3.5 h-3.5 text-slate-500" />
                  <span className="text-sm font-medium text-[#0A192F]">{formatWeekRange(hoursWeekStart)}</span>
                </div>
                <Button variant="outline" size="sm" onClick={() => navigateHoursWeek(1)} data-testid="hours-next-week">
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {hoursLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => <div key={i} className="h-10 bg-slate-100 rounded animate-pulse" />)}
                </div>
              ) : !hoursData?.comparison?.length ? (
                <p className="text-slate-500 text-center py-6">No employee data for this week.</p>
              ) : (
                <>
                  {/* Summary cards */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Scheduled</p>
                      <p className="text-xl font-black text-[#0A192F]" data-testid="total-scheduled-hours">{hoursData.totals.scheduled_hours}h</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Actual</p>
                      <p className="text-xl font-black text-[#0D9488]" data-testid="total-actual-hours">{hoursData.totals.actual_hours}h</p>
                    </div>
                    <div className={`rounded-lg p-3 text-center ${hoursData.totals.variance > 0 ? 'bg-emerald-50' : hoursData.totals.variance < 0 ? 'bg-red-50' : 'bg-slate-50'}`}>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Variance</p>
                      <p className={`text-xl font-black flex items-center justify-center gap-1 ${hoursData.totals.variance > 0 ? 'text-[#10B981]' : hoursData.totals.variance < 0 ? 'text-[#E11D48]' : 'text-slate-500'}`} data-testid="total-variance-hours">
                        {hoursData.totals.variance > 0 ? <ArrowUpRight className="w-4 h-4" /> : hoursData.totals.variance < 0 ? <ArrowDownRight className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
                        {Math.abs(hoursData.totals.variance)}h
                      </p>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50 hover:bg-slate-50">
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Employee</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Type</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Scheduled</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Actual</TableHead>
                          <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold text-right">Variance</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(() => {
                          const visible = hoursData.comparison.filter(r => (r.scheduled_hours || 0) > 0 || (r.actual_hours || 0) > 0);
                          if (visible.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={5} className="text-center text-slate-400 py-6 text-sm">No scheduled or actual hours for this week.</TableCell>
                              </TableRow>
                            );
                          }
                          return visible.map(row => (
                          <TableRow key={row.employee_id} data-testid={`hours-row-${row.employee_id}`}>
                            <TableCell className="font-medium text-[#0A192F]">{row.employee_name}</TableCell>
                            <TableCell className="capitalize text-slate-500 text-sm">{row.pay_type}</TableCell>
                            <TableCell className="text-right font-medium">{row.scheduled_hours}h</TableCell>
                            <TableCell className="text-right font-medium text-[#0D9488]">{row.actual_hours}h</TableCell>
                            <TableCell className={`text-right font-bold flex items-center justify-end gap-1 ${row.variance > 0 ? 'text-[#10B981]' : row.variance < 0 ? 'text-[#E11D48]' : 'text-slate-400'}`}>
                              {row.variance > 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : row.variance < 0 ? <ArrowDownRight className="w-3.5 h-3.5" /> : null}
                              {row.variance > 0 ? '+' : ''}{row.variance}h
                            </TableCell>
                          </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Quick Pay Sheet (uses shared QuickPayForm — identical to Payments page) */}
      <Sheet open={showPaySheet} onOpenChange={setShowPaySheet}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Record Payment</SheetTitle>
            <SheetDescription>
              Record a payment for an employee. Quick pay in under 3 clicks!
            </SheetDescription>
          </SheetHeader>
          {showPaySheet && (
            <QuickPayForm
              employees={employees}
              onSuccess={() => { setShowPaySheet(false); fetchKPIs(); }}
              onCancel={() => setShowPaySheet(false)}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Balance Audit Sheet — recompute & spot-check every employee */}
      <BalanceAuditSheet
        open={showAuditSheet}
        onOpenChange={setShowAuditSheet}
        data={auditData}
        loading={auditLoading}
        includeInactive={auditInactive}
        onToggleInactive={(v) => setAuditInactive(v)}
        onLoad={async (includeInactive) => {
          setAuditLoading(true);
          setAuditData(null);
          try {
            const res = await apiClient.get(`/payroll/balance-audit${includeInactive ? "?include_inactive=true" : ""}`);
            setAuditData(res.data);
          } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load audit");
          } finally {
            setAuditLoading(false);
          }
        }}
      />

      {/* Add Time Entry Dialog */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Time Entry</DialogTitle>
            <DialogDescription>Record hours worked for an employee</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTimeSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={timeForm.employee_id} onValueChange={(v) => setTimeForm({ ...timeForm, employee_id: v })}>
                <SelectTrigger data-testid="dash-time-employee"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Date *</Label><Input type="date" value={timeForm.worked_date} onChange={(e) => setTimeForm({ ...timeForm, worked_date: e.target.value })} required data-testid="dash-time-date" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Hours Worked *</Label><Input type="number" step="0.5" min="0" max="24" value={timeForm.worked_hours} onChange={(e) => setTimeForm({ ...timeForm, worked_hours: e.target.value })} required data-testid="dash-time-hours" /></div>
              <div className="space-y-2"><Label>Break Hours</Label><Input type="number" step="0.5" min="0" max="8" value={timeForm.break_hours} onChange={(e) => setTimeForm({ ...timeForm, break_hours: e.target.value })} data-testid="dash-time-break" /></div>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowTimeDialog(false)}>Cancel</Button>
              <Button type="submit" disabled={savingAction || !timeForm.employee_id || !timeForm.worked_hours} className="bg-[#0D9488] hover:bg-[#14B8A6]" data-testid="dash-time-submit">{savingAction ? "Saving..." : "Save Entry"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

// ---- Main Dashboard (role-based routing) ----
const Dashboard = () => {
  const { user } = useAuth();
  const isEmployee = user?.role === "employee";
  return isEmployee ? <EmployeeDashboard /> : <ManagerDashboard />;
};

export default Dashboard;
